import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { PombaseAPIService, GeneSummary, IdNameAndOrganism, GoCamSummary } from '../pombase-api.service';
import { CompleteService, SolrTermSummary, SolrRefSummary, SolrAlleleSummary } from '../complete.service';
import { getAppConfig, ConfigOrganism } from '../config';

import { TypeaheadMatch } from 'ngx-bootstrap/typeahead/typeahead-match.class';
import { switchMap } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { DeployConfigService } from '../deploy-config.service';
import { Util } from '../shared/util';

class SearchSummary {
  constructor(
    public uniquename: string,
    public uniquenameLowerCase: string,
    public name: string|undefined,
    public nameLowerCase: string|undefined,
    public product: string|undefined,
    public productLowerCase: string|undefined,
    public uniprotIdentifier: string|undefined,
    public uniprotIdentifierLowerCase: string|undefined,
    public transcripts: Array<string>,
    public lowerCaseTranscriptIds: Array<string>,
    public lowerCaseTranscriptNames: Array<string>,
    public synonyms: Array<string>,
    public synonymsLowerCase: Array<string>,
    public orthologs: Array<IdNameAndOrganism>,
    public organism: ConfigOrganism) { }
}

class DisplayModel {
  constructor(public matchType: string,
              public uniquename: string,
              public name: string|undefined,
              public otherDetails: Array<string>,
              public alleleGeneUniquename?: string,
              public organism?: ConfigOrganism) {
    if (this.name && this.name.length > 55) {
      this.name = this.name.slice(0, 52) + '...';
    }
    if (this.otherDetails) {
      this.otherDetails =
        this.otherDetails.filter(detail => detail && detail.length > 0);
      for (let idx = 0; idx < this.otherDetails.length; idx++) {
        const otherDetail = this.otherDetails[idx];
        if (otherDetail.length > 73 && otherDetail.indexOf('<span') === -1) {
          this.otherDetails[idx] = otherDetail.slice(0, 70) + '...';
        }
      }
    }
  }
}

interface FieldAndOrth {
  matchingFieldValue: string;
  orth: IdNameAndOrganism;
}

@Component({
    selector: 'app-search-box',
    templateUrl: './search-box.component.html',
    styleUrls: ['./search-box.component.css'],
    standalone: false
})
export class SearchBoxComponent implements OnInit {
  dataSource: Observable<Array<DisplayModel>>;
  noResults = false;
  waitingForServer = false;

  fieldValue = '';

  geneSummariesFailed = false;

  searchSummaries: Array<SearchSummary> = [];
  gocamSummaries: Array<GoCamSummary> = [];

  appConfig = getAppConfig();

  configuredTaxonIds = this.appConfig.organisms.map(org => org.taxonid);

  goCvNamesForTermComplete = '(' + this.appConfig.goCvNames.join(' OR ') + ')';
  otherCvNamesForTermComplete = '(' + this.appConfig.searchBoxCvNames.join(' OR ') + ') ';

  sysGeneIdRe = new RegExp(this.appConfig.gene_systematic_identifier_re);
  sysTranscriptIdRe = new RegExp(this.appConfig.transcript_systematic_identifier_re);

  @ViewChild('searchBox') searchBoxElement: ElementRef;

  constructor(private completeService: CompleteService,
              private pombaseApiService: PombaseAPIService,
              private deployConfigService: DeployConfigService,
              private router: Router) {
  }

  isInitialised(): boolean {
    return this.searchSummaries.length > 0;
  }

  private makeGeneDisplayModel(uniquename: string, name: string|undefined, otherDetailsArg: Array<string>,
                               organism: ConfigOrganism): DisplayModel {
    let otherDetails: Array<string> = [...otherDetailsArg];
    if (getAppConfig().isMultiOrganismMode()) {
      const orgDetails =
        `<span class="search-box-organism">${organism.common_name}</span>`;
      otherDetails.unshift(orgDetails);
    }

    return new DisplayModel('Matching genes:', uniquename, name, otherDetails, undefined, organism);
  }

  private highlightMatch(pos: number, searchString: string, target?: string): string {
    let start;
    let highlightBit;
    let rest;
    if (target) {
      start = target.substr(0, pos);
      highlightBit = target.substr(pos, searchString.length);
      rest = target.substr(pos + searchString.length);
    } else {
      start = '';
      highlightBit = searchString;
      rest = '';
    }
    return `${start}<span class="search-box-highlight">${highlightBit}</span>${rest}`;
  }

  private makeGoTermDisplayModel(termResult: SolrTermSummary): DisplayModel {
    let details = termResult.definition;

    if (termResult.highlighting && termResult.highlighting['definition']) {
      details = termResult.highlighting['definition'];
    }
    return new DisplayModel('Matching Gene Ontology terms:', termResult.termid, termResult.name,
                            [details]);
  }
  private makeOtherTermDisplayModel(termResult: SolrTermSummary): DisplayModel {
    let details = termResult.definition;

    if (termResult.highlighting && termResult.highlighting['definition']) {
      details = termResult.highlighting['definition'];
    }
    return new DisplayModel('Other matching terms:', termResult.termid, termResult.name,
      [details]);
  }

  private makeRefDisplayModel(refResult: SolrRefSummary): DisplayModel {
    let authorsAbbrev = refResult.authors_abbrev;
    let citation = refResult.citation;

    if (refResult.highlighting) {
      if (refResult.highlighting['authors_abbrev']) {
        authorsAbbrev = refResult.highlighting['authors_abbrev']
      }
      if (refResult.highlighting['citation']) {
        citation = refResult.highlighting['citation'];
      }
    }

    const authorAndCitation =
      [authorsAbbrev, citation].filter(v => !!v).join(' ');

    return new DisplayModel('Matching publications:', refResult.pubmedid, refResult.title,
                            [authorAndCitation]);
  }

  private makeAlleleDisplayModel(alleleResult: SolrAlleleSummary): DisplayModel {
    let nameAndDescription = Util.alleleDisplayName(alleleResult);

    let geneDisplayName = 'Gene: ';
    if (alleleResult.gene_name) {
      geneDisplayName += alleleResult.gene_name;
    } else {
      geneDisplayName += alleleResult.gene_uniquename;
    }

    let otherDetails = [geneDisplayName];

    let fieldValue = this.fieldValue.trim().replace('Δ', 'delta').toLowerCase();

    if (this.fieldValue && alleleResult.synonyms &&
        alleleResult.synonyms.find(syn => syn.toLowerCase() === fieldValue)) {
      otherDetails.push("Matching synonym: " + this.fieldValue);
    }

    return new DisplayModel('Matching alleles:', alleleResult.id, nameAndDescription,
                            otherDetails, alleleResult.gene_uniquename);
  }

  private nameExactMatch(geneSumm: SearchSummary, value: string): DisplayModel|undefined {
    if (geneSumm.nameLowerCase === value) {
      const matchingOrthologs = this.findMatchingOrthologs(geneSumm, value, 'exact');

      const orthOrgsToShow = this.appConfig.searchBoxConfig.ortholog_organisms_to_always_show;

      const details: Array<string> =
        matchingOrthologs.
          filter(orthMatch => orthOrgsToShow.includes(orthMatch.orth.taxonid)).
          map(({ matchingFieldValue, orth }) =>
               this.formatOrthologDetails(orth, matchingFieldValue));
      return this.makeGeneDisplayModel(geneSumm.uniquename, geneSumm.name, details, geneSumm.organism);
    }
    return undefined;
  }

  private identifierExactMatch(geneSumm: SearchSummary, value: string): DisplayModel|undefined {
    if (geneSumm.uniquenameLowerCase === value) {
      return this.makeGeneDisplayModel(geneSumm.uniquename, geneSumm.name, [], geneSumm.organism);
    } else {
      return undefined;
    }
  }

  private nameMatch(geneSumm: SearchSummary, value: string): DisplayModel|undefined {
    if (geneSumm.nameLowerCase && geneSumm.nameLowerCase.indexOf(value) !== -1 &&
        (geneSumm.name!.indexOf('-antisense-') === -1 ||
         value.indexOf('antisense') !== -1)) { // See #409
        return this.makeGeneDisplayModel(geneSumm.uniquename, geneSumm.name, [], geneSumm.organism);
    } else {
      return undefined;
    }
  }

  private identifierMatch(geneSumm: SearchSummary, value: string): DisplayModel|undefined {
    if (geneSumm.uniquenameLowerCase.indexOf(value) !== -1) {
      return this.makeGeneDisplayModel(geneSumm.uniquename, geneSumm.name, [], geneSumm.organism);
    } else {
      return undefined;
    }
  }

  private antisenseNameMatch(geneSumm: SearchSummary, value: string): DisplayModel|undefined {
    if (geneSumm.nameLowerCase && geneSumm.nameLowerCase.indexOf(value) !== -1 &&
        geneSumm.name!.indexOf('-antisense-') !== -1 &&
        value.indexOf('antisense') === -1) { // See #409
        return this.makeGeneDisplayModel(geneSumm.uniquename, geneSumm.name, [], geneSumm.organism);
    } else {
      return undefined;
    }
  }

  private transcriptMatch(geneSumm: SearchSummary, value: string): DisplayModel|undefined {
    const matchIndex = geneSumm.lowerCaseTranscriptIds.findIndex(id => id === value);
    if (matchIndex !== -1) {
      const highlightedMatch = this.highlightMatch(0, geneSumm.transcripts[matchIndex]);
      return this.makeGeneDisplayModel(geneSumm.uniquename, geneSumm.name,
                                       ['transcript: ' + highlightedMatch],
                                       geneSumm.organism);
    }
    const nameMatchIndex = geneSumm.lowerCaseTranscriptNames.findIndex(name => name === value);
    if (nameMatchIndex !== -1) {
      const highlightedMatch = this.highlightMatch(0, geneSumm.transcripts[nameMatchIndex]);
      return this.makeGeneDisplayModel(geneSumm.uniquename, geneSumm.name,
        ['transcript: ' + highlightedMatch],
        geneSumm.organism);
    }
    return undefined;
  }

  private synonymMatch(geneSumm: SearchSummary, value: string): DisplayModel|undefined {
    const matchIndex = geneSumm.synonymsLowerCase.findIndex(syn => syn.indexOf(value) !== -1);
    if (matchIndex !== -1) {
      const highlightedMatch = this.highlightMatch(0, geneSumm.synonyms[matchIndex]);
      return this.makeGeneDisplayModel(geneSumm.uniquename, geneSumm.name,
                                       ['synonym: ' + highlightedMatch],
                                       geneSumm.organism);
    }
    return undefined;
  }

  private synonymExactMatch(geneSumm: SearchSummary, value: string): DisplayModel|undefined {
    const matchIndex = geneSumm.synonymsLowerCase.findIndex(syn => syn === value);
    if (matchIndex !== -1) {
      const highlightedMatch = this.highlightMatch(0, geneSumm.synonyms[matchIndex]);
      return this.makeGeneDisplayModel(geneSumm.uniquename, geneSumm.name,
                                       ['synonym: ' + highlightedMatch],
                                       geneSumm.organism);
    }
    return undefined;
  }


  private findMatchingOrthologs(geneSumm: SearchSummary, value: string, exactness: 'exact' | 'sub-string'): Array<FieldAndOrth> {

    let matchingOrthologs  = [];

    if (exactness === 'exact') {
      for (const orth of geneSumm.orthologs) {
        if (!this.configuredTaxonIds.includes(orth.taxonid)) {
          continue;
        }
        if (orth.name && orth.name.toLowerCase() === value) {
          matchingOrthologs.push({ matchingFieldValue: orth.name, orth });
        } else {
          if (orth.identifier.toLowerCase() === value) {
            matchingOrthologs.push({ matchingFieldValue: orth.identifier, orth });
          } else {
            if (orth.secondary_identifier &&
              orth.secondary_identifier.toLowerCase() === value) {
              matchingOrthologs.push({ matchingFieldValue: orth.secondary_identifier, orth });
            }
          }
        }
      }
    } else {
      for (const orth of geneSumm.orthologs) {
        if (!this.configuredTaxonIds.includes(orth.taxonid)) {
          continue;
        }
        if (orth.name && orth.name.toLowerCase().indexOf(value) !== -1) {
          matchingOrthologs.push({ matchingFieldValue: orth.name, orth });
          break;
        }
      }
    }

    return matchingOrthologs;
  }

  private orthologMatch(geneSumm: SearchSummary, value: string, exactness: 'exact'|'sub-string'): DisplayModel|undefined {

    let matchingOrthologs = this.findMatchingOrthologs(geneSumm, value, exactness);

    if (matchingOrthologs.length > 0) {
      const details =
        matchingOrthologs.map(({ matchingFieldValue, orth })  => {
          return this.formatOrthologDetails(orth, matchingFieldValue);
        });
      return this.makeGeneDisplayModel(geneSumm.uniquename, geneSumm.name,
        details, geneSumm.organism);
    }

    return undefined;
  }

  private formatOrthologDetails(orth: IdNameAndOrganism, matchingFieldValue: string) {
    const orgDetails = this.appConfig.getOrganismByTaxonid(orth.taxonid);
    const commonNameSpan = `<span class="search-box-organism">(${orgDetails.common_name})</span>`;
    return `ortholog: ${this.highlightMatch(0, matchingFieldValue)} ${commonNameSpan}`;
  }

  private productMatch(geneSumm: SearchSummary, value: string): DisplayModel|undefined {
    if (geneSumm.product) {
      const pos = geneSumm.productLowerCase!.indexOf(value);
      if (pos !== -1) {
        const highlightedMatch = this.highlightMatch(pos, value, geneSumm.product);
        return this.makeGeneDisplayModel(geneSumm.uniquename, geneSumm.name,
                                         ['product: ' + highlightedMatch],
                                         geneSumm.organism);
      }
    }
    return undefined;
  }

  private uniprotIdMatch(geneSumm: SearchSummary, value: string): DisplayModel|undefined {
    if (geneSumm.uniprotIdentifier) {
      const pos = geneSumm.uniprotIdentifierLowerCase!.indexOf(value);

      if (pos !== -1) {
        const highlightedMatch = this.highlightMatch(pos, value, geneSumm.uniprotIdentifier);
        return this.makeGeneDisplayModel(geneSumm.uniquename, geneSumm.name,
                                         ['UniProt ID: ' + highlightedMatch],
                                         geneSumm.organism);
      }
    }
    return undefined;
  }

  private containsMatch(matches: Array<DisplayModel>, match: DisplayModel): boolean {
    return matches.findIndex((element) => element.uniquename === match.uniquename) !== -1;
  }

  private summariesFromToken(fieldValue: string): Array<DisplayModel> {
    if (this.searchSummaries) {
      let value = fieldValue.trim().toLowerCase();

      value = value.replace(/\s+gene$/i, '');
      value = value.replace(/^gene\s+/i, '');

      if (value.length > 0) {
        let filteredSummaries: Array<DisplayModel> = [];

        const maybeAdd = (match?: DisplayModel) => {
          if (match && filteredSummaries.length < 20 &&
              !this.containsMatch(filteredSummaries, match)) {
            filteredSummaries.push(match);
          }
        };

        for (let geneSumm of this.searchSummaries) {
          maybeAdd(this.nameExactMatch(geneSumm, value));
        }
        for (let geneSumm of this.searchSummaries) {
          maybeAdd(this.identifierExactMatch(geneSumm, value));
        }
        for (let geneSumm of this.searchSummaries) {
          maybeAdd(this.synonymExactMatch(geneSumm, value));
        }
        for (let geneSumm of this.searchSummaries) {
          maybeAdd(this.orthologMatch(geneSumm, value, 'exact'));
        }
        for (let geneSumm of this.searchSummaries) {
          maybeAdd(this.nameMatch(geneSumm, value));
        }
        if (filteredSummaries.length < 20) {
          let valueNoSuffixes = value;
          const suffixesToTrim = this.appConfig.searchBoxConfig.suffixes_to_trim;
          if (suffixesToTrim) {
            suffixesToTrim.map(suff => {
              valueNoSuffixes = valueNoSuffixes.replace(new RegExp(suff + '$'), '');
            });
          }
          for (let geneSumm of this.searchSummaries) {
            maybeAdd(this.identifierMatch(geneSumm, valueNoSuffixes));
          }
        }
        for (let geneSumm of this.searchSummaries) {
          maybeAdd(this.antisenseNameMatch(geneSumm, value));
        }
        if (filteredSummaries.length < 20) {
          for (let geneSumm of this.searchSummaries) {
            maybeAdd(this.transcriptMatch(geneSumm, value));
          }
        }
        if (filteredSummaries.length < 20) {
          for (let geneSumm of this.searchSummaries) {
            maybeAdd(this.synonymMatch(geneSumm, value));
          }
        }
        if (filteredSummaries.length < 20) {
          for (let geneSumm of this.searchSummaries) {
            maybeAdd(this.orthologMatch(geneSumm, value, 'sub-string'));
          }
        }
        if (filteredSummaries.length < 20) {
          for (let geneSumm of this.searchSummaries) {
            maybeAdd(this.productMatch(geneSumm, value));
          }
        }
        if (filteredSummaries.length < 20) {
          for (let geneSumm of this.searchSummaries) {
            maybeAdd(this.uniprotIdMatch(geneSumm, value));
          }
        }
        return filteredSummaries;
      }
    }
    return [];
  }

  getGoCamMatches(token: string): Observable<Array<DisplayModel>> {
    let ret = [];
    const tokenSplit = token.split(/ +/);
    const matchAll = (s: string) => {
      for (const bit of tokenSplit) {
        if (!s.includes(bit)) {
          return false;
        }
      }
      return true;
    };
    for (const gocam of this.gocamSummaries) {
      if (gocam.gocam_id == token || matchAll(gocam.title)) {
        ret.push(new DisplayModel('Matching GO-CAM pathway:', gocam.gocam_id, gocam.title, []))
      }
    }

    return of(ret);
  }

  getGoTermMatches(token: string): Observable<Array<DisplayModel>> {
    return this.completeService.completeTermName(this.goCvNamesForTermComplete, token)
      .pipe(map((termResults: Array<SolrTermSummary>) =>
        termResults.map(termResult => this.makeGoTermDisplayModel(termResult))),
        catchError(e => {
          console.log('completion API call failed: ' + e.message);
          return of([]);
        }));
  }

  getOtherTermMatches(token: string): Observable<Array<DisplayModel>> {
    return this.completeService.completeTermName(this.otherCvNamesForTermComplete, token)
      .pipe(map((termResults: Array<SolrTermSummary>) =>
        termResults.map(termResult => this.makeOtherTermDisplayModel(termResult))),
        catchError(e => {
          console.log('completion API call failed: ' + e.message);
          return of([]);
        }));
  }

  getRefMatches(token: string): Observable<Array<DisplayModel>> {
    return this.completeService.completeRef(token)
      .pipe(map((refResults: Array<SolrRefSummary>) =>
           refResults.map(refResult => this.makeRefDisplayModel(refResult))),
            catchError(e => {
              console.log('completion API call failed: ' + e.message);
              return of([]);
            }));
  }

  getAlleleMatches(token: string): Observable<Array<DisplayModel>> {
    token = token.replace('Δ', 'delta');
    const trimToken = token.trim().toLowerCase();
    return this.completeService.completeAllele(token)
      .pipe(
        map((alleleResults: Array<SolrAlleleSummary>) => {
          const nameMatches =
            alleleResults.filter(alleleSummary => {
              if (alleleSummary.name &&
                  alleleSummary.name.toLowerCase() === trimToken) {
                    return true;
              }
              if (alleleSummary.synonyms &&
                  alleleSummary.synonyms.find(syn => syn.toLowerCase() === trimToken)) {
                return true;
              }
              if (alleleSummary.id.toLowerCase() === trimToken) {
                return true;
              }
              return false;
            });
          if (nameMatches.length > 0) {
            alleleResults = nameMatches;
          }
          return alleleResults.map(alleleResult => this.makeAlleleDisplayModel(alleleResult));
        }),
        catchError(e => {
          console.log('completion API call failed: ' + e.message);
          return of([]);
        }));
  }

  observableFromToken(token: string): Observable<Array<DisplayModel>> {
    token = token.trim();

    const databaseName = this.appConfig.database_name.toLowerCase();

    if (token.toLowerCase().startsWith(databaseName + ':')) {
      token = token.substring(databaseName.length + 1);
    }

    let observables = [];
    const geneSummaryObservable = of(this.summariesFromToken(token));
    observables.push(geneSummaryObservable);

    token = token.replace(this.sysGeneIdRe, '').replace(this.sysTranscriptIdRe, '').trim();

    // handle copy and paste from PubMed pages, and other weird things that happen to PMIDs
    token = token.replace(/^(PMID|PubMed)[: _\-]*/i, 'PMID:');

    if (token.length > 0) {
      // for now we filter out systematic IDs because they cause Lucene problems

      const gocamObservable = this.getGoCamMatches(token);
      const goTermResultsObservable = this.getGoTermMatches(token);
      const otherTermResultsObservable = this.getOtherTermMatches(token);
      const refResultsObservable = this.getRefMatches(token);

      observables.push(gocamObservable);
      observables.push(goTermResultsObservable);
      observables.push(otherTermResultsObservable);
      observables.push(refResultsObservable);

      if (!this.deployConfigService.productionMode()) {
        const alleleResultsObservable = this.getAlleleMatches(token);
        observables.push(alleleResultsObservable);
      }
    }

    const combined =
      combineLatest(observables)
        .pipe(
          map(([geneRes, gocamRes, goTermRes, otherTermRes, refRes, alleleRes]) => {
            const maxGenes = 5;
            const maxGoCams = 2;
            const maxGoTerms = 3;
            const maxOtherTerms = 3;
            const maxAlleles = 1;
            const geneCount = geneRes.length;

            if (!gocamRes) {
              gocamRes = [];
            }
            if (!goTermRes) {
              goTermRes = [];
            }
            if (!otherTermRes) {
              otherTermRes = [];
            }
            if (!refRes) {
              refRes = [];
            }
            if (!alleleRes) {
              alleleRes = [];
            }

            const goTermCount = goTermRes.length;
            const otherTermCount = otherTermRes.length;
            let refCount = 4;
            if (geneCount + goTermCount + otherTermCount < maxGenes + maxGoTerms + maxOtherTerms) {
              refCount += (maxGenes + maxGoTerms + maxOtherTerms) - (geneCount + goTermCount + otherTermCount);
            }

            this.waitingForServer = false;

            if (this.deployConfigService.productionMode()) {
              return [...geneRes.slice(0, maxGenes),
                      ...gocamRes.slice(0, maxGoCams),
                      ...goTermRes.slice(0, maxGoTerms),
                      ...otherTermRes.slice(0, maxOtherTerms),
                      ...refRes.slice(0, refCount)];
            } else {
              return [...geneRes.slice(0, maxGenes),
                      ...gocamRes.slice(0, maxGoCams),
                      ...alleleRes.slice(0, maxAlleles),
                      ...goTermRes.slice(0, maxGoTerms),
                      ...otherTermRes.slice(0, maxOtherTerms),
                      ...refRes.slice(0, refCount)];
             }
          }));
    return combined;
  }

  getDataSource(): Observable<Array<DisplayModel>> {
    return Observable
      .create((observer: any) => {
        // Runs on every search
        observer.next(this.fieldValue);
      })
      .pipe(
        catchError(err => {
          this.waitingForServer = false;
          return of([]);
        }),
        switchMap((token: string) => {
          this.waitingForServer = true;
          return this.observableFromToken(token);
        })
      );
  }

  ngOnInit() {
    this.dataSource = this.getDataSource();

    this.pombaseApiService.getGeneSummariesPromise()
      .then(summaries => {
        this.searchSummaries = this.makeSearchSummaries(summaries);

        let summaryCmp =
          (a: SearchSummary, b: SearchSummary) => {
            if (a.name) {
              if (b.name) {
                return a.name.localeCompare(b.name);
              } else {
                return -1;
              }
            } else {
              if (b.name) {
                return 1;
              } else {
                return a.uniquename.localeCompare(b.uniquename);
              }
            }
          };

        this.searchSummaries.sort(summaryCmp);

        setTimeout(() => { // this will make the execution after the above boolean has changed
          this.searchBoxElement.nativeElement.focus();
        }, 100);
      })
      .catch(reason => this.geneSummariesFailed = true);


    this.pombaseApiService.getAllGoCamSummaries()
      .then(summaries => {
        this.gocamSummaries = summaries;
      })
  }

  makeSearchSummaries(summaries: Array<GeneSummary>): Array<SearchSummary> {
    return summaries
      .map(geneSumm => {
        const nameLowerCase =
          geneSumm.name ? geneSumm.name.toLowerCase() : undefined;
        const productLowerCase =
          geneSumm.product ? geneSumm.product.toLowerCase() : undefined;
        const uniprotIdentifierLowerCase =
          geneSumm.uniprot_identifier ? geneSumm.uniprot_identifier.toLowerCase() : undefined;

        let transcripts = [];
        let lowerCaseTranscriptIds = [];
        let lowerCaseTranscriptNames = [];

        for (let transcriptNum = 1;
             transcriptNum <= geneSumm.getTranscriptCount();
             transcriptNum++) {
          const transcriptId = geneSumm.uniquename + '.' + transcriptNum;
          transcripts.push(transcriptId);
          lowerCaseTranscriptIds.push(transcriptId.toLowerCase());

          if (geneSumm.name) {
            const transcriptName = geneSumm.name + '.' + transcriptNum;
            lowerCaseTranscriptNames.push(transcriptName);
          }
        }

        return new SearchSummary(geneSumm.uniquename,
                                 geneSumm.uniquename.toLowerCase(),
                                 geneSumm.name, nameLowerCase,
                                 geneSumm.product, productLowerCase,
                                 geneSumm.uniprot_identifier, uniprotIdentifierLowerCase,
                                 transcripts, lowerCaseTranscriptIds,
                                 lowerCaseTranscriptNames,
                                 geneSumm.synonyms, geneSumm.synonyms.map(syn => syn.toLowerCase()),
                                 geneSumm.orthologs, geneSumm.organism)
      });
  }

  public typeaheadOnSelect(e: TypeaheadMatch): void {
    const displayModel = e.item as DisplayModel;
    if (displayModel.matchType.toLowerCase().includes('genes')) {
      this.router.navigate(['/gene', displayModel.uniquename]);
    } else {
      if (e.item.matchType.toLowerCase().includes('term')) {
        this.router.navigate(['/term', displayModel.uniquename]);
      } else {
        if (e.item.matchType.toLowerCase().includes('publication')) {
          this.router.navigate(['/reference', displayModel.uniquename]);
        } else {
          if (e.item.matchType.toLowerCase().includes('go-cam')) {
            this.router.navigate(['/gocam/pombase-view/docs', displayModel.uniquename]);
          } else {
            if (this.deployConfigService.productionMode()) {
              this.router.navigate(['/gene', displayModel.alleleGeneUniquename]);
            } else {
              this.router.navigate(['/allele', displayModel.uniquename]);
            }
          }
        }
      }
    }
    this.clearBox();
  }

  public typeaheadNoResults(e: boolean) {
    this.noResults = e;
  }

  matchesReference(value: string): boolean {
    return value.match(/^\s*(?:(?:pmid|pubmed|go_ref)(?:[:_]| +))?\d\d\d+\s*$/i) != null;
  }

  matchesTerm(value: string): boolean {
    return value.match(/^\s*[a-zA-Z_]+:\d\d\d+\s*$/) != null;
  }

  matchesGoCamId(value: string): boolean {
    return value.match(/^(?:gomodel:)?[0-9a-f]{16}$/) != null;
  }

  clearBox(): void {
    this.fieldValue = '';
  }

  enterPressed() {
    let trimmedValue = this.fieldValue
        .replace(/\s+$/, '').replace(/^\s+/, '');

    if (this.matchesReference(trimmedValue)) {
      let pmid = trimmedValue.replace(/^(?:pmid|pubmed)(?:[:_]| +)/i, 'PMID:');
      if (!pmid.startsWith('PMID:')) {
        pmid = 'PMID:' + pmid;
      }
      this.clearBox()
      this.router.navigate(['/reference', pmid]);
    } else {
      if (this.matchesTerm(trimmedValue)) {
        this.clearBox()
        this.router.navigate(['/term', trimmedValue]);
      } else {
        if (this.matchesGoCamId(trimmedValue)) {
          const gocamDetailPromise = this.pombaseApiService.getGoCamDetailByIds(trimmedValue);
          gocamDetailPromise.then((_) => {
            this.clearBox();
            this.router.navigate(['/gocam/pombase-view/docs/', trimmedValue]);
          });
        }
      }
    }
  }

  noMatches(): boolean {
    return !this.waitingForServer && this.noResults && this.fieldValue.length > 0;
  }
}
